// C:\Users\aleja\Code\inventory_analytics\src\front\src\pages\WarehouseCostAnalytics.jsx

import React, { useContext, useEffect, useState } from "react";
import { Context } from "../store/appContext";
import "../styles/layout.css";
import "../styles/utilities.css";
import "../styles/forms.css";
import "../styles/warehouseCostAnalytics.css";

const currencyFormatter = new Intl.NumberFormat("es-ES", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const numberFormatter = new Intl.NumberFormat("es-ES", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const getTodayKey = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getMonthStartKey = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");

  return `${year}-${month}-01`;
};

const defaultFilters = {
  date_from: getMonthStartKey(),
  date_to: getTodayKey(),
  material_id: "",
  supplier: "",
};

const formatCurrency = (value) => currencyFormatter.format(Number(value || 0));

const formatNumber = (value) => numberFormatter.format(Number(value || 0));

const hasVisibleResults = (analytics) => {
  if (!analytics) {
    return false;
  }

  return (
    Number(analytics?.summary?.purchase_lines || 0) > 0 ||
    Number(analytics?.summary?.inventory_count_movements || 0) > 0
  );
};

const WarehouseCostAnalytics = () => {
  const { store, actions } = useContext(Context);
  const [filters, setFilters] = useState(defaultFilters);

  const analytics = store.warehouseCostAnalytics;
  const loading = store.warehouseCostAnalyticsLoading;
  const errorMessage = store.warehouseCostAnalyticsError;

  useEffect(() => {
    actions.getWarehouseCostAnalytics(defaultFilters);
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFilters((currentFilters) => ({
      ...currentFilters,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await actions.getWarehouseCostAnalytics(filters);
  };

  const handleClear = async () => {
    setFilters(defaultFilters);
    await actions.getWarehouseCostAnalytics(defaultFilters);
  };

  const summary = analytics?.summary || {};
  const spendByMaterial = Array.isArray(analytics?.spend_by_material)
    ? analytics.spend_by_material
    : [];
  const spendBySupplier = Array.isArray(analytics?.spend_by_supplier)
    ? analytics.spend_by_supplier
    : [];
  const inventorySummary = analytics?.inventory_counts?.summary || {};
  const inventoryByMaterial = Array.isArray(analytics?.inventory_counts?.by_material)
    ? analytics.inventory_counts.by_material
    : [];

  return (
    <div className="container mt-5 warehouse-cost-analytics-page">
      <div className="page-header warehouse-cost-analytics-header">
        <div>
          <h1 className="page-title">Analítica de coste de almacén</h1>
          <p className="page-subtitle">
            Gasto real por repuestos desde clean layer y recuentos separados como
            actividad no económica.
          </p>
        </div>
      </div>

      <form className="warehouse-cost-filters" onSubmit={handleSubmit}>
        <div className="warehouse-cost-filter-group">
          <label htmlFor="warehouse_cost_date_from">Desde</label>
          <input
            id="warehouse_cost_date_from"
            name="date_from"
            type="date"
            value={filters.date_from}
            onChange={handleChange}
          />
        </div>

        <div className="warehouse-cost-filter-group">
          <label htmlFor="warehouse_cost_date_to">Hasta</label>
          <input
            id="warehouse_cost_date_to"
            name="date_to"
            type="date"
            value={filters.date_to}
            onChange={handleChange}
          />
        </div>

        <div className="warehouse-cost-filter-group">
          <label htmlFor="warehouse_cost_material_id">Material</label>
          <input
            id="warehouse_cost_material_id"
            name="material_id"
            type="text"
            value={filters.material_id}
            onChange={handleChange}
            placeholder="ID material"
          />
        </div>

        <div className="warehouse-cost-filter-group">
          <label htmlFor="warehouse_cost_supplier">Proveedor</label>
          <input
            id="warehouse_cost_supplier"
            name="supplier"
            type="text"
            value={filters.supplier}
            onChange={handleChange}
            placeholder="Proveedor"
          />
        </div>

        <div className="warehouse-cost-filter-actions">
          <button className="btn btn-primary" type="submit" disabled={loading}>
            Aplicar
          </button>
          <button
            className="btn btn-secondary"
            type="button"
            onClick={handleClear}
            disabled={loading}
          >
            Limpiar
          </button>
        </div>
      </form>

      {loading && (
        <div className="warehouse-cost-state warehouse-cost-state-loading">
          Cargando analítica de coste de almacén...
        </div>
      )}

      {!loading && errorMessage && (
        <div className="warehouse-cost-state warehouse-cost-state-error">
          {errorMessage}
        </div>
      )}

      {!loading && !errorMessage && !hasVisibleResults(analytics) && (
        <div className="warehouse-cost-state warehouse-cost-state-empty">
          No hay movimientos económicos ni recuentos para los filtros seleccionados.
        </div>
      )}

      {!loading && !errorMessage && hasVisibleResults(analytics) && (
        <>
          <section className="warehouse-cost-kpis">
            <article className="warehouse-cost-kpi warehouse-cost-kpi-spend">
              <span className="warehouse-cost-kpi-label">Gasto total</span>
              <strong>{formatCurrency(summary.total_spend)}</strong>
            </article>

            <article className="warehouse-cost-kpi">
              <span className="warehouse-cost-kpi-label">Repuestos comprados</span>
              <strong>{formatNumber(summary.distinct_materials_purchased)}</strong>
            </article>

            <article className="warehouse-cost-kpi">
              <span className="warehouse-cost-kpi-label">Proveedores implicados</span>
              <strong>{formatNumber(summary.distinct_suppliers)}</strong>
            </article>

            <article className="warehouse-cost-kpi warehouse-cost-kpi-counts">
              <span className="warehouse-cost-kpi-label">Movimientos de recuento</span>
              <strong>{formatNumber(summary.inventory_count_movements)}</strong>
              <small>
                IN {formatNumber(summary.inventory_count_in_movements)} / OUT{" "}
                {formatNumber(summary.inventory_count_out_movements)}
              </small>
            </article>
          </section>

          <section className="warehouse-cost-card">
            <div className="warehouse-cost-section-head">
              <div>
                <h2>Gasto por repuesto</h2>
                <p>Solo líneas `purchase_reposition` agregadas en backend.</p>
              </div>
            </div>

            <div className="table-responsive warehouse-cost-table-scroll">
              <table className="table table-compact mb-0">
                <thead>
                  <tr>
                    <th>Material</th>
                    <th>Descripción</th>
                    <th className="text-end">Gasto total</th>
                    <th className="text-end">Cantidad total</th>
                    <th className="text-end">Precio medio</th>
                    <th className="text-end">Líneas</th>
                    <th className="text-end">Proveedores</th>
                  </tr>
                </thead>
                <tbody>
                  {spendByMaterial.length === 0 && (
                    <tr>
                      <td className="table-empty" colSpan="7">
                        No hay compras para los filtros seleccionados.
                      </td>
                    </tr>
                  )}

                  {spendByMaterial.map((row) => (
                    <tr key={`${row.material_id || "no-material"}-${row.description || "no-description"}`}>
                      <td className="cell-strong">{row.material_id || "-"}</td>
                      <td>{row.description || "-"}</td>
                      <td className="text-end">{formatCurrency(row.total_spend)}</td>
                      <td className="text-end">{formatNumber(row.total_quantity)}</td>
                      <td className="text-end">
                        {formatCurrency(row.average_unit_price)}
                      </td>
                      <td className="text-end">{formatNumber(row.purchase_lines)}</td>
                      <td className="text-end">{formatNumber(row.supplier_count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="warehouse-cost-card">
            <div className="warehouse-cost-section-head">
              <div>
                <h2>Gasto por proveedor</h2>
                <p>Ranking de gasto agregado por proveedor.</p>
              </div>
            </div>

            <div className="table-responsive warehouse-cost-table-scroll">
              <table className="table table-compact mb-0">
                <thead>
                  <tr>
                    <th>Proveedor</th>
                    <th className="text-end">Gasto total</th>
                    <th className="text-end">Líneas</th>
                    <th className="text-end">Materiales distintos</th>
                    <th className="text-end">Cantidad total</th>
                  </tr>
                </thead>
                <tbody>
                  {spendBySupplier.length === 0 && (
                    <tr>
                      <td className="table-empty" colSpan="5">
                        No hay proveedores con gasto para los filtros seleccionados.
                      </td>
                    </tr>
                  )}

                  {spendBySupplier.map((row) => (
                    <tr key={row.supplier || "sin-proveedor"}>
                      <td className="cell-strong">{row.supplier || "-"}</td>
                      <td className="text-end">{formatCurrency(row.total_spend)}</td>
                      <td className="text-end">{formatNumber(row.purchase_lines)}</td>
                      <td className="text-end">
                        {formatNumber(row.distinct_materials)}
                      </td>
                      <td className="text-end">{formatNumber(row.total_quantity)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="warehouse-cost-card warehouse-cost-card-counts">
            <div className="warehouse-cost-section-head warehouse-cost-section-head-counts">
              <div>
                <h2>Recuentos</h2>
                <p>Actividad no económica separada del gasto real.</p>
              </div>
              <div className="warehouse-cost-count-summary">
                <span>IN: {formatNumber(inventorySummary.inventory_count_in_movements)}</span>
                <span>
                  OUT: {formatNumber(inventorySummary.inventory_count_out_movements)}
                </span>
                <span>
                  Artículos afectados:{" "}
                  {formatNumber(inventorySummary.distinct_materials_affected)}
                </span>
              </div>
            </div>

            <div className="table-responsive warehouse-cost-table-scroll">
              <table className="table table-compact mb-0">
                <thead>
                  <tr>
                    <th>Material</th>
                    <th>Descripción</th>
                    <th className="text-end">Count IN</th>
                    <th className="text-end">Count OUT</th>
                    <th className="text-end">Neto</th>
                  </tr>
                </thead>
                <tbody>
                  {inventoryByMaterial.length === 0 && (
                    <tr>
                      <td className="table-empty" colSpan="5">
                        No hay recuentos para los filtros seleccionados.
                      </td>
                    </tr>
                  )}

                  {inventoryByMaterial.map((row) => (
                    <tr key={`${row.material_id || "no-material"}-${row.description || "no-description"}-counts`}>
                      <td className="cell-strong">{row.material_id || "-"}</td>
                      <td>{row.description || "-"}</td>
                      <td className="text-end">
                        {formatNumber(row.inventory_count_in_movements)}
                      </td>
                      <td className="text-end">
                        {formatNumber(row.inventory_count_out_movements)}
                      </td>
                      <td className="text-end">{formatNumber(row.net_quantity)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default WarehouseCostAnalytics;
